import { Injectable } from '@angular/core';
import { Observable, Subject, timer } from 'rxjs';
import { DataStorage } from '../data/data-storage';
import { HttpClient } from '@angular/common/http';
import { getApiUrl } from '../helpers/rest-utils';
import { concatMap, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Business } from '../business/business';
import { Feature, Subscription, SubscriptionPlanName, SubscriptionStatus } from '../subscription/subscription';
import { buildBusiness } from '../business/business-mapper';
import { merge } from '@rallycommerce/common/utils.js';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from '../auth/authentication-service';
import { SessionService } from '../session.service';
import { Router } from '@angular/router';
import { RegistrationPlatform } from '../registration-platform';
import { OnboardingIntent } from '../business/business';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  persistentStorage: DataStorage = DataStorage.persistent();
  businessUsers: User[] = [];
  user: User;
  constructor(private http: HttpClient, private auth: AuthenticationService, private session: SessionService, private router: Router) {
    // This service is a root singleton, so the cached user outlives a logout
    // when the next login/signup happens by in-app navigation (no reload).
    // AuthenticationGuard trusts the cache without refetching, so a stale user
    // from the previous account would pass the guard and stamp its business ID
    // onto the new account's requests. Drop it with the session.
    this.session.terminate.subscribe(() => this.clear());
  }

  get userData(): User {
    return this.user;
  }

  clear(): void {
    this.user = null;
    this.businessUsers = [];
  }

  get activeBusiness(): Business {
    const activeBusiness = this.userData?.businesses && this.userData?.businesses.length > 0 ? this.userData?.businesses[0] : null;
    return this.userData?.business || activeBusiness || null;
  }

  get name(): string {
    return this.userData?.name;
  }

  get email(): string {
    return this.userData?.email;
  }

  get id(): string {
    return this.userData?.id;
  }

  get subscription(): Subscription {
    return this.userData?.subscription;
  }

  get agent(): any {
    return this.userData?.agent;
  }

  get hasCreditCardOnFile(): boolean {
    return this.userData?.subscription?.isCreditCardAdded;
  }

  get isTestMode(): boolean {
    return !this.userData?.subscription?.isCreditCardAdded && this.isUserOnFreeMinutesBillingVersion;
  }

  get hasTrialExpired(): boolean {
    // Only treat as "trial expired" when the sub actually had a trial that has
    // ended. Without the trialEndsAt check, an incomplete subscription that the
    // API remaps to past_due (initial trial-setup payment pending 3DS or
    // processing) would also trigger this and incorrectly open the trial-
    // extension modal.
    return this.userData?.subscription?.status === SubscriptionStatus.PastDue
      && !!this.userData?.subscription?.trialEndsAt
      && !this.hasCreditCardOnFile
      && this.isUserOnTrialBasedBilling;
  }

  get isUserOnFreeMinutesBillingVersion(): boolean {
    return this.activeBusiness?.billingVersion === 2;
  }

  get isUserOn7DayFreeTrialVersion(): boolean {
    return this.activeBusiness?.billingVersion === 3;
  }

  get isUserOnBillingV4(): boolean {
    return this.activeBusiness?.billingVersion === 4;
  }

  get isUserOnTrialBasedBilling(): boolean {
    return this.activeBusiness?.billingVersion === 3 || this.activeBusiness?.billingVersion === 4;
  }

  get hasMultipleBusinesses(): boolean {
    return this.userData?.hasMultipleBusinesses;
  }

  get isEligibleForWtPromo(): boolean {
    return this.activeBusiness?.billingVersion !== 4;
  }

  get isAccountLimited(): boolean {
    const userCreatedDate = new Date(this.userData?.createdAt);
    const releaseDate = new Date('2024-11-06T10:00:00+01:00');
    const featureReadyForTestingDate = new Date('2024-10-24T00:00:00Z');
    return (userCreatedDate >= featureReadyForTestingDate && environment.name !== 'production') || (userCreatedDate >= releaseDate && environment.name === 'production');
  }

  get hasSpamEnabled(): boolean {
    return true;
  }

  get hasAppointmentsEnabled(): boolean {
    return this.userData?.subscription?.features?.includes(Feature.Appointments);
  }

  get hasAppointmentSchedulingEnabled(): boolean {
    return this.userData?.subscription?.features?.includes(Feature.AppointmentScheduling);
  }

  get hasTransferCallsEnabled(): boolean {
    return this.userData?.subscription?.features?.includes(Feature.TransferCalls);
  }

  get hasWaterfallTransfersEnabled(): boolean {
    return this.userData?.subscription?.features?.includes(Feature.WaterfallTransfers);
  }

  get hasTrainingFilesEnabled(): boolean {
    return this.userData?.subscription?.features?.includes(Feature.TrainingFiles);
  }

  get hasTextLinksEnabled(): boolean {
    return this.userData?.subscription?.features?.includes(Feature.TextLinks);
  }

  get hasSmsConversationsEnabled(): boolean {
    return this.userData?.subscription?.features?.includes(Feature.SmsConversations);
  }

  get isShopifySignup(): boolean {
    return this.activeBusiness?.registrationSource === RegistrationPlatform.SHOPIFY;
  }

  get hasCommerceApp(): boolean {
    return !!this.activeBusiness?.hasCommerceApp || this.isShopifySignup;
  }

  get hasCompletedShopifyAccountSetup(): boolean {
    return !!this.userData?.hasCompletedShopifyAccountSetup;
  }

  // The single gate for the unified Train → Customize → Go Live onboarding
  // (ENG-2292). The API resolves the flag, commerce apps and mid-flow stamps
  // server-side, so the admin only ever checks this one value.
  get isUnifiedOnboardingFlow(): boolean {
    return this.activeBusiness?.onboardingFlow === 'unified';
  }

  get onboardingIntent(): OnboardingIntent {
    return this.activeBusiness?.meta?.onboardingIntent || {};
  }

  // Lowercase plan key from the API ('scale' | 'professional').
  get recommendedPlan(): string {
    return this.activeBusiness?.recommendedPlan;
  }

  refreshUser(user?: User, overrideInsteadOfMerge = false) {
    if (user) {
      const business = user?.businesses?.[0] || user?.business;
      user.business = buildBusiness(business);
      let refreshedUser = overrideInsteadOfMerge ? user : merge(this.userData, user);
      if (refreshedUser.subscription?.status === SubscriptionStatus.Canceled) {
        refreshedUser.subscription.planName = SubscriptionPlanName.None as any;
      }
      refreshedUser.isActive = true;
      this.user = refreshedUser;
    }
  }

  getUser(): Observable<User> {
    return this.http.get(`${getApiUrl()}user`)
      .pipe(map((res: any) => res.data), tap((user: User) => this.refreshUser(user)));
  }

  // Variant B/D collect the card at signup, but it attaches in a queued job — so
  // the first /user read after the card step can still show no card on file,
  // briefly surfacing the "go live to accept external calls" trial banner on the
  // dashboard. Poll (~30s max) until the card is confirmed so the banner clears
  // without a manual refresh. Lives here (not in a component) so it survives the
  // navigation into /setup. Safe to call once per card submit.
  pollForRegistrationCard(): void {
    timer(2500, 2500).pipe(
      take(12),
      switchMap(() => this.getUser()),
      filter((user: User) => !!user?.subscription?.isCreditCardAdded),
      take(1),
    ).subscribe({ error: () => { } });
  }

  logout() {
    this.auth.revoke();
    this.user = null;
  }

  searchBusinesses(term: string): Observable<Business[]> {
    const params = {};
    if (term) {
      params['q'] = term;
    }
    return this.http
      .get(`${getApiUrl()}businesses`, { params })
      .pipe(map((res: any) => res.data || []));
  }

  switchToBusiness(id: string): Observable<any> {
    return this.http
      .post(`${getApiUrl()}businesses/${id}/activate`, {})
      .pipe(
        map((res: any) => res.data),
        tap((data) => {
          const user = data?.user || this.userData;
          user.business = buildBusiness(data?.business);
          user.agent = data?.agent;
          user.hasMultipleBusinesses = true;
          this.refreshUser(user, true);
          this.session.refreshState.next(user);
        }));
  }

  createBusiness(data: AccountData): Observable<any> {
    return this.http.post(`${getApiUrl()}businesses`, data).pipe(map((res: any) => res.data));
  }

  updateUser(data: Partial<UpdateUserData>): Observable<any> {
    return this.http.patch(`${getApiUrl()}user`, data).pipe(map((res: any) => res.data));
  }
}

export interface UpdateUserData {
  meta?: {
    referralSource?: string;
  };
}

export interface User {
  name?: string;
  email: string;
  isActive?: boolean;
  id?: string;
  inviteId?: string;
  business?: Business;
  businesses?: Business[];
  agent?: any;
  subscription?: Subscription;
  hasMultipleBusinesses?: boolean;
  createdAt?: string;
  statusId?: number;
  lastActive?: string;
  emailVerifiedAt?: string;
  hasCompletedShopifyAccountSetup?: boolean;
  referralSource?: string;
  signupVariant?: string;
  role?: {
    title: UserRole;
    description: string
  };
  campaign?: {
    rrf: string;
    rcmp: string;
  }
}

export enum UserRole {
  Admin = 'admin',
  Owner = 'owner',
  Viewer = 'viewer'
}

export interface AccountData {
  placeId: string;
  url: string;
}